import {
  LabelObjectOrientation,
  LabelObjectPlainTextPosition,
  LabelTemplateDPIResolution,
  type LabelTemplateDTO,
  LabelTemplateObjectAlignmentType,
  type LabelTemplateObjectDTO,
  LabelTemplateObjectType,
  LabelTemplateTextType,
} from "@/api/classes/labelTemplate/labelTemplate.dto";
import type { BaseProductDto } from "@/types/product/productTypes";
import type { PartnerDto } from "@/api/api.dto";
import {
  getKeyByDomainObjectPropertyType,
  LabelTemplateBarcodeTypeTranslation,
  orderTypeByDomainObjectPropertyType,
} from "@/api/classes/labelTemplate/labelTemplateTranslates";
import { selectPrinterModal } from "@/tools/selectPrinterModal";
import { createNotification } from "@/tools/notification";
import { useQzTrayStore } from "@/stores/qzTray/qzTrayStore";
import { MarketPlace } from "@/types/market-place";
import JsBarcode from "jsbarcode";
import {
  BASE64_SVG_BOX,
  BASE64_SVG_FBS_WB,
  BASE64_SVG_SUPPLY,
} from "@/stores/labelTemplate/constants/templates";
// @ts-ignore
import { mediaUrl } from "../../../../env.d.ts";
import * as pdfjsLib from "pdfjs-dist/legacy/build/pdf";
import fbsOzonPdfExample from "/src/assets/fbs-ozon-example.pdf";
import { usePartner } from "@/stores/partner/partner";

pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/build/pdf.worker.min.mjs",
  import.meta.url,
).toString();

const DPI_BY_ENUM: Record<LabelTemplateDPIResolution, number> = {
  [LabelTemplateDPIResolution.ThreeHundred]: 300,
  [LabelTemplateDPIResolution.TwoHundredAndThree]: 203,
};

const BARCODE_FIELD_BY_MARKETPLACE: Record<
  MarketPlace,
  keyof BaseProductDto | null
> = {
  [MarketPlace.ChestniyZnak]: null,
  [MarketPlace.Wildberries]: "wbBarcode",
  [MarketPlace.Ozon]: "ozonBarcode",
  [MarketPlace.YandexMarket]: "yandexBarcode",
};

const DEFAULT_BASE_64_BY_TYPE: Partial<
  Record<LabelTemplateObjectType, string>
> = {
  [LabelTemplateObjectType.BoxBarcode]: BASE64_SVG_BOX,
  [LabelTemplateObjectType.FbsWBBarcode]: BASE64_SVG_FBS_WB,
  [LabelTemplateObjectType.SupplyBarcode]: BASE64_SVG_SUPPLY,
  [LabelTemplateObjectType.FbsOzonBarcode]: fbsOzonPdfExample,
};

export class LabelTemplate {
  canvasRef: HTMLCanvasElement = document.createElement("canvas");
  ctx = this.canvasRef.getContext("2d");

  private qzTrayStore = useQzTrayStore();
  private partnerStore = usePartner();

  get dpiLabel() {
    return DPI_BY_ENUM[this.label.labelDPIResolution];
  }

  private imageCache = new Map<string, HTMLImageElement>();
  private pdfCanvasCache = new Map<string, HTMLCanvasElement>();

  constructor(
    public readonly label: LabelTemplateDTO,
    private readonly product?: BaseProductDto,
    private readonly stringOfBarcodeImage?: string,
    private readonly barcodeMarketplace?: {
      marketplace?: MarketPlace;
      barcode?: string;
    },
  ) {
    this.canvasRef.width = this.convertMmToPx(label.labelWidthInMm);
    this.canvasRef.height = this.convertMmToPx(label.labelHeightInMm);
  }

  async printLabel(
    printerName = this.label.attachedPrinterName,
    count: number = 1,
  ) {
    if (!printerName || printerName === "null") {
      printerName = await selectPrinterModal();
    }

    if (printerName) {
      await this.renderLabel();

      try {
        await this.qzTrayStore.printLabelTemplate(this, count, printerName);
      } catch (error) {
        console.error("Error printing label:", error);
      }
    }
  }

  async renderLabel() {
    this.ctx?.clearRect(0, 0, this.canvasRef.width, this.canvasRef.height);

    for (const labelTemplateObject of this.label.labelTemplateObjects) {
      await this.addItemToCanvas(labelTemplateObject);
    }
  }

  async downloadLabel(): Promise<void> {
    await this.renderLabel();

    const link = document.createElement("a");

    link.download = `${this.label.labelTemplateName}.png`;
    link.href = this.canvasRef.toDataURL("image/png");

    link.click();
  }

  private saveCanvas() {
    this.ctx?.save();
  }

  private restoreCanvas() {
    this.ctx?.restore();
  }

  private async addItemToCanvas(item: LabelTemplateObjectDTO) {
    switch (item.objectType) {
      case LabelTemplateObjectType.Barcode:
        await this.addBarcodeToCanvas(item);
        break;
      case LabelTemplateObjectType.Text:
      case LabelTemplateObjectType.CurrentDateAndTimeOfTheUserMachine:
        this.addTextToCanvas(item);
        break;
      case LabelTemplateObjectType.SupplyBarcode:
      case LabelTemplateObjectType.BoxBarcode:
      case LabelTemplateObjectType.FbsWBBarcode:
      case LabelTemplateObjectType.FbsOzonBarcode:
        await this.addImageBarcodeToCanvas(item);
        break;
      case LabelTemplateObjectType.Logo:
        await this.addLogoToCanvas(item);
        break;
    }
  }

  private addTextToCanvas(item: LabelTemplateObjectDTO) {
    if (!this.ctx) return;

    const {
      textFont,
      textSize,
      enableLineBreaks,
      numberOfMaximumNumberOfLines,
      leftMargin,
      topMargin,
      rightMargin,
      bottomMargin,
      labelObjectOrientation,
      alignmentType,
    } = item;

    const text = this.getTextFromItem(item);
    if (!text) return;

    const left = this.convertMmToPx(leftMargin ?? 0);
    const top = this.convertMmToPx(topMargin ?? 0);
    const right = this.convertMmToPx(rightMargin ?? 0);
    const bottom = this.convertMmToPx(bottomMargin ?? 0);

    let targetWidth = this.canvasRef.width - left - right;
    let targetHeight = this.canvasRef.height - top - bottom;

    if (
      labelObjectOrientation === LabelObjectOrientation.UpDown ||
      labelObjectOrientation === LabelObjectOrientation.DownUp
    ) {
      [targetWidth, targetHeight] = [targetHeight, targetWidth];
    }

    this.saveCanvas();
    this.ctx.font = `${textSize}px ${textFont}`;
    this.ctx.fillStyle = "#000";

    let lines: string[] = [];

    if (enableLineBreaks) {
      const words = text.split(" ");
      let currentLine = "";

      for (const word of words) {
        const testLine =
          currentLine.length > 0 ? `${currentLine} ${word}` : word;
        const { width: testWidth } = this.ctx.measureText(testLine);

        if (testWidth > targetWidth) {
          if (currentLine.length > 0) {
            lines.push(currentLine);
            currentLine = word;
          } else {
            lines.push(testLine);
            currentLine = "";
          }
        } else {
          currentLine = testLine;
        }
      }
      if (currentLine.length > 0) {
        lines.push(currentLine);
      }
    } else {
      lines.push(text);
    }

    if (
      numberOfMaximumNumberOfLines &&
      lines.length > numberOfMaximumNumberOfLines
    ) {
      lines = lines.slice(0, numberOfMaximumNumberOfLines);
    }

    const lineHeight = textSize * 1.2;

    this.ctx.translate(left, top);

    if (labelObjectOrientation === LabelObjectOrientation.UpDown) {
      this.ctx.rotate(Math.PI / 2);
      this.ctx.translate(0, -targetHeight);
    } else if (labelObjectOrientation === LabelObjectOrientation.DownUp) {
      this.ctx.rotate(-Math.PI / 2);
      this.ctx.translate(-targetWidth, 0);
    }

    let startX = 0;
    if (alignmentType === LabelTemplateObjectAlignmentType.Centered) {
      this.ctx.textAlign = "center";
      startX = targetWidth / 2;
    } else if (alignmentType === LabelTemplateObjectAlignmentType.Right) {
      this.ctx.textAlign = "right";
      startX = targetWidth;
    } else {
      this.ctx.textAlign = "left";
    }

    this.ctx.textBaseline = "top";

    lines.forEach((line, i) => {
      const y = i * lineHeight;
      this.ctx!.fillText(line, startX, y);
    });

    this.restoreCanvas();
  }

  private async addImageToCanvas(
    item: LabelTemplateObjectDTO,
    image: HTMLImageElement,
  ) {
    if (!this.ctx) return;

    const {
      leftMargin,
      topMargin,
      bottomMargin,
      rightMargin,
      barcodeSize,
      labelObjectOrientation,
    } = item;

    const left = this.convertMmToPx(leftMargin ?? 0);
    const top = this.convertMmToPx(topMargin ?? 0);
    const right = this.convertMmToPx(rightMargin ?? 0);
    const bottom = this.convertMmToPx(bottomMargin ?? 0);

    const workWidth = this.canvasRef.width - left - right;
    const workHeight = this.canvasRef.height - top - bottom;

    this.saveCanvas();

    const scale = barcodeSize && barcodeSize > 0 ? barcodeSize : 1;
    const imgWidth = image.width * scale;
    const imgHeight = image.height * scale;

    let drawX = left;
    let drawY = top;

    if (labelObjectOrientation === LabelObjectOrientation.UpDown) {
      this.ctx.translate(left + workWidth, top);
      this.ctx.rotate(Math.PI / 2);
      drawX = 0;
      drawY = 0;
    } else if (labelObjectOrientation === LabelObjectOrientation.DownUp) {
      this.ctx.translate(left, top + workHeight);
      this.ctx.rotate(-Math.PI / 2);
      drawX = 0;
      drawY = 0;
    }

    this.ctx.imageSmoothingEnabled = true;
    this.ctx.imageSmoothingQuality = "high";
    this.ctx.drawImage(image, drawX, drawY, imgWidth, imgHeight);

    this.restoreCanvas();
  }

  private async addBarcodeToCanvas(item: LabelTemplateObjectDTO) {
    let barcode: string = "";

    if (this.product) {
      const marketplace = this.barcodeMarketplace?.marketplace;
      const inputBarcode = this.barcodeMarketplace?.barcode;

      const marketplaceExists =
        marketplace !== null && marketplace !== undefined;

      if (marketplaceExists || inputBarcode) {
        if (marketplaceExists) {
          const field = BARCODE_FIELD_BY_MARKETPLACE[marketplace];

          if (field) {
            barcode = this.product[field]?.toString() || "";
          }
        }

        if (inputBarcode) {
          barcode = inputBarcode;
        }
      } else {
        const barcodeWb = BARCODE_FIELD_BY_MARKETPLACE[MarketPlace.Wildberries];
        const barcodeOzon = BARCODE_FIELD_BY_MARKETPLACE[MarketPlace.Ozon];

        let tempBarcode;

        if (barcodeWb) {
          tempBarcode = this.product[barcodeWb];
        } else if (barcodeOzon) {
          tempBarcode = this.product[barcodeOzon];
        }

        barcode = Array.isArray(tempBarcode)
          ? tempBarcode[0]
          : typeof tempBarcode === "string"
            ? tempBarcode
            : "";
      }
    }

    const {
      leftMargin,
      topMargin,
      barcodeSize,
      barcodeHeightInMm,
      displayBarcodeText,
      barcodeType,
      labelObjectOrientation,
      textSize,
    } = item;

    if (this.product && barcode) {
      const barcodeText = barcode;
      const height = this.convertMmToPx(barcodeHeightInMm);
      const width = this.convertMmToPx(barcodeSize) || 1;
      const format = LabelTemplateBarcodeTypeTranslation[barcodeType];
      const text = displayBarcodeText || "";
      const tempCanvas = document.createElement("canvas");
      const options = {
        format,
        height,
        width,
        text,
        background: "transparent",
        displayValue: !!barcodeText,
        fontSize: textSize,
      };

      try {
        JsBarcode(tempCanvas, barcodeText, options);
      } catch (e1) {
        try {
          JsBarcode(tempCanvas, barcodeText, { ...options, format: "CODE128" });
        } catch (e2) {
          createNotification("error", (e2 as Error).message);
          return;
        }
      }

      this.ctx?.save();

      const x = leftMargin ?? 0;
      const y = topMargin ?? 0;

      const cx = this.convertMmToPx(x) + tempCanvas.width / 2;
      const cy = this.convertMmToPx(y) + tempCanvas.height / 2;

      this.ctx?.translate(cx, cy);

      if (labelObjectOrientation) {
        this.ctx?.rotate((labelObjectOrientation * Math.PI) / 180);
      }

      this.ctx?.drawImage(
        tempCanvas,
        -tempCanvas.width / 2,
        -tempCanvas.height / 2,
      );

      this.ctx?.restore();
    }
  }

  private convertMmToPx(mm: number) {
    return (mm / 25.4) * this.dpiLabel;
  }

  private getTextFromItem(item: LabelTemplateObjectDTO) {
    const {
      objectType,
      textType,
      plainText,
      plainTextPosition,
      domainObjectPropertyTypes,
    } = item;
    let text: string = "";

    if (
      objectType === LabelTemplateObjectType.CurrentDateAndTimeOfTheUserMachine
    ) {
      text = new Date().toLocaleString();
    }

    if (objectType === LabelTemplateObjectType.Text) {
      if (textType === LabelTemplateTextType.PlainText) {
        text = plainText;
      } else {
        let textArray = [plainText];

        const domainArray = domainObjectPropertyTypes?.length
          ? domainObjectPropertyTypes
          : [];

        const domainTextArray = domainArray.map((i) => {
          let tempText;

          const orderType = orderTypeByDomainObjectPropertyType[i];
          const key = getKeyByDomainObjectPropertyType(orderType, i);

          if (orderType === "product" && this.product) {
            const typedKey = key as keyof BaseProductDto;
            tempText = this.product[typedKey];
          }

          if (orderType === "partner" && this.partnerStore.partner) {
            const typedKey = key as keyof PartnerDto;
            tempText = this.partnerStore.partner[typedKey];
          }

          tempText = tempText && typeof tempText === "string" ? tempText : "";

          return tempText;
        });

        if (plainTextPosition === LabelObjectPlainTextPosition.Left) {
          textArray = [plainText, ...domainTextArray].filter(Boolean);
        }

        if (plainTextPosition === LabelObjectPlainTextPosition.Right) {
          textArray = [...domainTextArray, plainText].filter(Boolean);
        }

        text = textArray.join(" ");
      }
    }

    return text;
  }

  private async getDataUri(base64Data: string): Promise<string | null> {
    if (base64Data.startsWith("iVBORw0KGgo")) {
      return `data:image/png;base64,${base64Data}`;
    }

    if (base64Data.startsWith("PD94bWwg") || base64Data.startsWith("PHN2ZyB")) {
      return `data:image/svg+xml;base64,${base64Data}`;
    }

    if (base64Data.startsWith("^XA")) {
      createNotification("error", "ZPL формат не поддерживается для canvas");
      return null;
    }

    return `data:image/svg+xml;base64,${base64Data}`;
  }

  private async addImageBarcodeToCanvas(item: LabelTemplateObjectDTO) {
    const base64Data = this.stringOfBarcodeImage
      ? this.stringOfBarcodeImage
      : DEFAULT_BASE_64_BY_TYPE[item.objectType];

    if (!base64Data) return;

    // Handle URLs (http or local assets) separately from raw base64 strings
    if (base64Data.startsWith("http") || base64Data.includes("/assets")) {
      await this.addUrlBarcodeToCanvas(item, base64Data);
      return;
    }

    const dataUri = await this.getDataUri(base64Data);

    if (!dataUri) {
      return;
    }

    const image = await this.getImageFromUrl(dataUri);

    await this.addImageToCanvas(item, image);
  }

  private async addUrlBarcodeToCanvas(
    item: LabelTemplateObjectDTO,
    url: string,
  ) {
    // Check if URL points to a PDF (by extension or by fetching and inspecting content type)
    const isPdf = await this.isUrlPdf(url);

    if (isPdf) {
      const pdfCanvas = await this.renderPdfUrlToCanvas(url);

      if (pdfCanvas) {
        await this.addCanvasImageToCanvas(item, pdfCanvas);
        return;
      }
    }

    // Non-PDF URL — load as a regular image
    const image = await this.getImageFromUrl(url);
    await this.addImageToCanvas(item, image);
  }

  private async isUrlPdf(url: string): Promise<boolean> {
    // Quick check by extension (strip query params)
    const pathname = new URL(url, window.location.origin).pathname;
    if (pathname.toLowerCase().endsWith(".pdf")) return true;

    // If no clear extension, do a HEAD request to check content type
    try {
      const headResponse = await fetch(url, { method: "HEAD" });
      const contentType = headResponse.headers.get("content-type") || "";
      return contentType.includes("application/pdf");
    } catch {
      return false;
    }
  }

  private async renderPdfUrlToCanvas(
    url: string,
  ): Promise<HTMLCanvasElement | null> {
    if (this.pdfCanvasCache.has(url)) {
      return this.pdfCanvasCache.get(url)!;
    }

    try {
      const response = await fetch(url);
      const arrayBuffer = await response.arrayBuffer();

      const loadingTask = pdfjsLib.getDocument({
        data: new Uint8Array(arrayBuffer),
      });
      const pdf = await loadingTask.promise;
      const page = await pdf.getPage(1);

      const maxCanvasPixels = 4_000_000;
      const baseScale = this.dpiLabel / 72;
      const baseViewport = page.getViewport({
        scale: baseScale,
        rotation: 0,
        dontFlip: false,
        offsetX: 0,
        offsetY: 0,
      });

      let scale = baseScale;
      if (baseViewport.width * baseViewport.height > maxCanvasPixels) {
        const reductionFactor = Math.sqrt(
          maxCanvasPixels / (baseViewport.width * baseViewport.height),
        );
        scale = baseScale * reductionFactor;
      }

      const viewport = page.getViewport({
        scale,
        rotation: 0,
        dontFlip: false,
        offsetX: 0,
        offsetY: 0,
      });

      const canvas = document.createElement("canvas");
      canvas.width = viewport.width;
      canvas.height = viewport.height;
      const context = canvas.getContext("2d")!;

      context.imageSmoothingEnabled = true;
      context.imageSmoothingQuality = "high";

      // @ts-ignore
      await page.render({ canvasContext: context, viewport }).promise;

      this.pdfCanvasCache.set(url, canvas);

      return canvas;
    } catch (error) {
      console.error("Failed to render PDF to canvas:", error);
      return null;
    }
  }

  private async addCanvasImageToCanvas(
    item: LabelTemplateObjectDTO,
    sourceCanvas: HTMLCanvasElement,
  ) {
    if (!this.ctx) return;

    const {
      leftMargin,
      topMargin,
      bottomMargin,
      rightMargin,
      barcodeSize,
      labelObjectOrientation,
    } = item;

    const left = this.convertMmToPx(leftMargin ?? 0);
    const top = this.convertMmToPx(topMargin ?? 0);
    const right = this.convertMmToPx(rightMargin ?? 0);
    const bottom = this.convertMmToPx(bottomMargin ?? 0);

    const workWidth = this.canvasRef.width - left - right;
    const workHeight = this.canvasRef.height - top - bottom;

    this.saveCanvas();

    const scale = barcodeSize && barcodeSize > 0 ? barcodeSize : 1;
    const imgWidth = sourceCanvas.width * scale;
    const imgHeight = sourceCanvas.height * scale;

    let drawX = left;
    let drawY = top;

    if (labelObjectOrientation === LabelObjectOrientation.UpDown) {
      this.ctx.translate(left + workWidth, top);
      this.ctx.rotate(Math.PI / 2);
      drawX = 0;
      drawY = 0;
    } else if (labelObjectOrientation === LabelObjectOrientation.DownUp) {
      this.ctx.translate(left, top + workHeight);
      this.ctx.rotate(-Math.PI / 2);
      drawX = 0;
      drawY = 0;
    }

    this.ctx.imageSmoothingEnabled = true;
    this.ctx.imageSmoothingQuality = "high";
    this.ctx.drawImage(sourceCanvas, drawX, drawY, imgWidth, imgHeight);

    this.restoreCanvas();
  }

  private async addLogoToCanvas(item: LabelTemplateObjectDTO) {
    const logo = this.partnerStore.partner?.imageLink;

    const url = logo
      ? logo.startsWith("http")
        ? logo
        : `${mediaUrl}/${logo}`
      : "";

    if (url) {
      const image = await this.getImageFromUrl(url);

      await this.addImageToCanvas(item, image);
    }
  }

  private async getImageFromUrl(url: string): Promise<HTMLImageElement> {
    if (this.imageCache.has(url)) {
      return this.imageCache.get(url)!;
    }

    const response = await fetch(url);
    const blob = await response.blob();

    const base64 = await this.blobToBase64(blob);

    const imageInstance = new Image();
    imageInstance.src = base64;

    await new Promise((resolve, reject) => {
      imageInstance.onload = resolve;
      imageInstance.onerror = reject;
    });

    this.imageCache.set(url, imageInstance);

    return imageInstance;
  }

  private blobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }
}
